import { Helper } from "../../core/util/helper";
import { HTTPService, JsonResultClass} from "../../core/util/httpManager";
import { Grid } from "../../core/components/grid";
import { Config } from "../../core/config/config";
import { DashboardStatistic } from "../../generalSettings/dashboard";
import { GridCustomToolBarButton } from "../../core/components/toolBar";
import { Entity } from "../../core/components/commonApp";
import { editDashboardStatistic } from "./editDialog";
import { DashboardDataServiceFactory, IDashboardDataService } from "./dashboardDataService";
import { openCopyDialog } from "./copyDialog";
import { NotificationHelper } from "../../core/util/notificationHelper";
import React from "react";

export interface DashboardEntity extends Entity {
    DashboardStatistics: DashboardStatistic[];
    CustomDashboardStatistics: DashboardStatistic[];
    User: {
        Name: string
    }
}

interface DashboardProps {
    entity: DashboardEntity;
    onChange: (field: { model: string }, value) => void;
    custom: boolean;
}

interface DashboardState {
    initialized: boolean;
    selected: string;
}

export default class Dashboard extends React.Component<DashboardProps, DashboardState>{
    private gridOptions: kendo.ui.GridOptions;
    private gridId = kendo.guid();
    private statisticNames = {};
    private dashboardDataService: IDashboardDataService;
    private standardDashboardDataService: IDashboardDataService;

    private OPEN_DIALOG = "open_dialog";
    private UP = "up";
    private DOWN = "down";
    private LOAD_DEFAULT: "load_default";
    private COPY = "copy";
    private model: string;

    constructor(props: DashboardProps) {
        super(props);
        this.state = {
            initialized: false,
            selected: null
        };
        this.init();
    }

    private init = async () => {
        if (this.props.custom) {
            this.dashboardDataService = await DashboardDataServiceFactory.getCustom();
            this.model = "entity.CustomDashboardStatistics";
        } else {
            this.dashboardDataService = await DashboardDataServiceFactory.getStandard();
            this.model = "entity.DashboardStatistics";
        }
        this.standardDashboardDataService = await DashboardDataServiceFactory.getStandard();
        const [statisticNames] = await Promise.all([
            this.getStatisticNames()
        ]);
        this.statisticNames = statisticNames;
        this.initGridOptions();
        this.setState({initialized: true})
    }

    private getStatisticNames = async () => {
        let dict = {};
        const result = await HTTPService.get<any[]>('Dashboard/GetAllGlobalStatistics');
        result.forEach((info) => {
            dict[info.Id] = info.Name;
        });
        return dict;
    }

    private initGridOptions = () => {
        let columns: kendo.ui.GridColumn[] = [];


        if (this.props.custom) {
            columns = [
                {
                    field: "StatisticId",
                    title: "StatisticId",
                    hidden: true
                },
                {
                    field: "Name",
                    title: Helper.getTranslation("WebSMS.UserSettings.Dashboard.Statistic"),
                    template: (dataItem: DashboardStatistic) => {
                        return `<span>${this.statisticNames[dataItem.StatisticId]}</span>`;
                    }
                },
                {
                    field: "LabelTranslationKey",
                    title: Helper.getTranslation("WebSMS.UserSettings.Dashboard.TabName"),
                    template: (dataItem: DashboardStatistic) => {
                        let name = "";
                        if (Config.settings.allowDashboardNameChange) {
                            name = Helper.getTranslation(dataItem.TabName) ?? dataItem.TabName;
                        } else {
                            name = this.standardDashboardDataService.getData(this.props.entity).find(ds => ds.StatisticId == dataItem.StatisticId)?.TabName;
                        }


                        return `<span>${name}</span>`;
                    }
                }
            ];

            if (Config.settings.allowDashboardNameChange) {
                columns.push({
                    field: "Og_Name",
                    title: Helper.getTranslation("WebSMS.UserSettings.Dashboard.OgName"),
                    template: (dataItem: DashboardStatistic) => {
                        const name = this.standardDashboardDataService.getData(this.props.entity).find(ds => ds.StatisticId == dataItem.StatisticId)?.TabName;
                        return `<span>${name}</span>`;
                    }
                });
            }

            columns.push({
                field: "Icon",
                title: Helper.getTranslation("WebSMS.ResourcePlanningScheduler.Icon.Icon"),
                width: 80,
                template: (dataItem: DashboardStatistic) => {
                    return `<div style='display: flex; justify-content: center; align-items: center;'><i class="fa-light fa-${dataItem.Icon} fa-2x"></i></div>`;
                }
            });

            columns.push({
                field: "Visible",
                title: Helper.getTranslation("WebCRM.EditIndividualCategories.Visible"),
                template: (dataItem: DashboardStatistic) => {
                    if (dataItem.Permission == 0) {
                        return `<div style='display: flex; justify-content: center; align-items: center;color: #d50000'><i class="fa-light fa-xmark fa-2x"></i></div>`;
                    } else {
                        return `<div style='display: flex; justify-content: center; align-items: center;color: #00c853'><i class="fa-light fa-check fa-2x"></i></div>`;
                    }
                }
            });

        } else {
            columns = [
                {
                    field: "StatisticId",
                    title: "StatisticId",
                    hidden: true
                },
                {
                    field: "Name",
                    title: Helper.getTranslation("WebSMS.UserSettings.Dashboard.Statistic"),
                    template: (dataItem: DashboardStatistic) => {
                        return `<span>${this.statisticNames[dataItem.StatisticId]}</span>`;
                    }
                },
                {
                    field: "LabelTranslationKey",
                    title: Helper.getTranslation("WebSMS.UserSettings.Dashboard.TabName"),
                    template: (dataItem: DashboardStatistic) => {
                        return `<span>${Helper.getTranslation(dataItem.TabName) ?? dataItem.TabName}</span>`;
                    }
                },
                {
                    field: "Icon",
                    title: Helper.getTranslation("WebSMS.ResourcePlanningScheduler.Icon.Icon"),
                    width: 80,
                    template: (dataItem: DashboardStatistic) => {
                        return `<div style='display: flex; justify-content: center; align-items: center;'><i class="fa-light fa-${dataItem.Icon} fa-2x"></i></div>`;
                    }
                },
                {
                    field: "Permission",
                    title: Helper.getTranslation("Dokument.Permission.Permission"),
                    template: (dataItem: DashboardStatistic) => {
                        return `<span>${Helper.getTranslation(`WebSMS.UserSettings.StandardRights.${dataItem.Permission}`)}</span>`;
                    }
                }
            ];
        }

        this.gridOptions = {
            sortable: false,
            pageable: false,
            groupable: false,
            filterable: false,
            selectable: true,
            resizable: true,
            scrollable: true,
            editable: false,
            columns: columns
        }

    }

    private onChange = (value: DashboardStatistic[]) => {
        this.props.onChange({ model: this.model }, value);
    }

    private getSelected = () => {
        const data = this.dashboardDataService.getData(this.props.entity);
        return data.find(ds => ds.Id == this.state.selected)
    }

    private renderGrid = () => {
        if (!this.state.initialized)
            return null;
        const data = this.dashboardDataService.getData(this.props.entity);
        const selected = this.getSelected();
        const buttons: Array<GridCustomToolBarButton> = [
            {
                iconClass: "fa-solid fa-pen",
                autoEnabledOnRecordSelected: false,
                caption: Helper.getTranslation("Standard.Editieren"),
                enabled: Helper.hasValue(selected),
                id: this.OPEN_DIALOG
            },
            {
                iconClass: "fa-solid fa-up",
                autoEnabledOnRecordSelected: false,
                caption: Helper.getTranslation("Standard.Up"),
                enabled: Helper.hasValue(selected) && selected.Pos > 0,
                id: this.UP
            },
            {
                iconClass: "fa-solid fa-down",
                autoEnabledOnRecordSelected: false,
                caption: Helper.getTranslation("Standard.Down"),
                enabled: Helper.hasValue(selected) && selected.Pos < (data.length - 1),
                id: this.DOWN
            }
        ];
        if (this.props.custom) {
            buttons.push({
                iconClass: "fa-solid fa-arrow-rotate-left",
                autoEnabledOnRecordSelected: false,
                caption: Helper.getTranslation("WebSMS.UserSettings.Dashboard.Reset"),
                enabled: Helper.hasValue(selected),
                id: this.LOAD_DEFAULT
            });
        } else {
            buttons.push({
                iconClass: "fa-solid fa-arrow-turn-right",
                autoEnabledOnRecordSelected: false,
                caption: Helper.getTranslation("Standard.Kopieren"),
                enabled: true,
                id: this.COPY
            });
        }

        return (
            <div className="flex-container" id={this.gridId}>
                <Grid
                    data={data}
                    options={this.gridOptions}
                    id={`dashboard_${this.props.custom}`}
                    customButtons={buttons}
                    onClickCustomButton={this.onButtonClick}
                    onSelectionChanged={this.onSelectionChanged}
                    selectFilter={Helper.hasValue(selected) ? selected.StatisticId : null}
                    selectFilterField={"StatisticId"}
                    throttleActive={false}
                />
            </div>
        );
    }

    private onSelectionChanged = (dataItem: DashboardStatistic) => {
        if (Helper.hasValue(dataItem))
            this.setState({ selected: dataItem.Id })
    }

    private onButtonClick = async (id: string) => {
        const selected = this.getSelected();
        switch (id) {
            case this.OPEN_DIALOG:
                await this.edit(selected);
                break;
            case this.UP:
                this.swap(selected.Pos, selected.Pos - 1);
                break;
            case this.DOWN:
                this.swap(selected.Pos, selected.Pos + 1);
                break;
            case this.LOAD_DEFAULT:
                this.loadDefault();
                break;
            case this.COPY:

                await this.copy();
                break;
                
        }
    }

    private copy = async () =>{
        const entities = await openCopyDialog(`0_${this.props.entity.User.Name}`);
        const dashboardStatistics = this.dashboardDataService.getData(this.props.entity)

        const { Code, Message } = await HTTPService.post<JsonResultClass>('Dashboard/Copy?dashboardStatisticsJSON=' + JSON.stringify(dashboardStatistics) + '&entitiesJSON=' + JSON.stringify(entities));
        if (Code == 0) {
            NotificationHelper.showSuccessMessage(Message)
        } else {
            NotificationHelper.showErrorMessage(Message)
        }
    }

    private loadDefault = () => {
        const selected = this.getSelected();
        const data = this.dashboardDataService.getData(this.props.entity);
        const index = data.findIndex(f => f.StatisticId == selected.StatisticId);
        const item = this.standardDashboardDataService.getData(this.props.entity).find(f => f.StatisticId == selected.StatisticId);
        data[index].Icon = item.Icon;
        data[index].TabName = item.TabName;
        this.onChange(data);
    }

    private swap = (posA: number, posB: number) => {
        const data = this.dashboardDataService.getData(this.props.entity);

        const indexA = data.findIndex(ds => ds.Pos == posA);
        const indexB = data.findIndex(ds => ds.Pos == posB);

        const itemA = data[indexA];
        const itemB = data[indexB];

        itemA.Pos = posB;
        itemB.Pos = posA;

        data[indexA] = itemB;
        data[indexB] = itemA;
        
        this.onChange(data);
    }

    private edit = async (dataItem: DashboardStatistic) => {
        dataItem = JSON.parse(JSON.stringify(dataItem));

        const data = this.dashboardDataService.getData(this.props.entity);
        const title = this.statisticNames[dataItem.StatisticId];
        //dataItem.TabName = title;
        const statistic = await editDashboardStatistic(dataItem, title, this.props.custom);
        if (statistic) {
            const index = data.findIndex(ds => ds.Id == statistic.Id)
            data[index] = statistic;
            this.onChange(data);
        }
    }

    render() {
        return this.renderGrid();
    }
}




